import * as React from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog"
import { AlertTriangle, Trash2, Info, Archive } from 'lucide-react'
import { cn } from "@/lib/utils"

/**
 * ConfirmationDialog - A reusable confirmation modal for destructive and important actions
 * 
 * Replaces window.confirm() with a styled, accessible dialog that:
 * - Provides consistent styling with the app design
 * - Allows loading states during async operations
 * - Enables rich content (icons, descriptions)
 * - Prevents accidental dismissal
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {function} props.onOpenChange - Callback when open state changes
 * @param {string} props.title - Dialog title (default: "Are you sure?")
 * @param {string|React.ReactNode} props.description - Dialog description/message
 * @param {string} props.confirmText - Text for confirm button (default: "Continue")
 * @param {string} props.cancelText - Text for cancel button (default: "Cancel")
 * @param {string} props.variant - Visual variant: "destructive" | "warning" | "info" (default: "destructive")
 * @param {function} props.onConfirm - Callback when confirm is clicked
 * @param {function} props.onCancel - Optional callback when cancel is clicked
 * @param {boolean} props.isLoading - Show loading state on confirm button
 * @param {string} props.testId - Optional data-testid for testing
 */
function ConfirmationDialog({
  open,
  onOpenChange,
  title = "Are you sure?",
  description,
  confirmText = "Continue",
  cancelText = "Cancel",
  variant = "destructive",
  icon,
  onConfirm,
  onCancel,
  isLoading = false,
  testId,
}) {
  const icons = {
    destructive: <Trash2 className="h-5 w-5 text-red-500" />,
    archive: <Archive className="h-5 w-5 text-red-500" />,
    warning: <AlertTriangle className="h-5 w-5 text-amber-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  }

  const confirmButtonStyles = {
    destructive: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500",
    archive: "bg-red-600 hover:bg-red-700 text-white focus:ring-red-500",
    warning: "bg-amber-600 hover:bg-amber-700 text-white focus:ring-amber-500",
    info: "bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500",
  }

  const handleConfirm = () => {
    if (!isLoading && onConfirm) {
      onConfirm()
    }
  }

  const handleCancel = () => {
    if (!isLoading) {
      if (onCancel) {
        onCancel()
      }
      onOpenChange(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid={testId}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {icon || icons[variant]}
            <span>{title}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-sm text-muted-foreground">
              {description}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel 
            onClick={handleCancel} 
            disabled={isLoading}
            data-testid={testId ? `${testId}-cancel` : undefined}
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className={cn(confirmButtonStyles[variant])}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Processing...
              </>
            ) : (
              confirmText
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

ConfirmationDialog.displayName = "ConfirmationDialog"

export { ConfirmationDialog }
